/**
 * SIMPLIFIED AUTH SYSTEM FOR WHITE RABBIT ACADEMY
 *
 * Simple role-based auth for subscription-based LMS.
 * 6 roles: free, premium, student, instructor, admin, suspended
 */

// ============================================================================
// SIMPLE ROLE SYSTEM - 6 CLEAR ROLES
// ============================================================================

/**
 * User role enumeration - exported for use in permissions and type checks
 * Members are used in object keys (ROLE_LEVELS, ROLE_LABELS, ROLE_DESCRIPTIONS)
 * and referenced throughout permission checks
 */

export enum UserRole {
  /**
   * FREE - Enrolled member with free access
   * Can: Browse catalog, view public content, basic member features
   */
  FREE = 'free',

  /**
   * PREMIUM - Paid subscription holder
   * Can: Access Chamber of Echoes, library, premium content, all courses
   */
  PREMIUM = 'premium',

  /**
   * STUDENT - Equity/scholarship student with granted access
   * Can: Same as Premium - full course access via equity program
   */
  STUDENT = 'student',

  /**
   * INSTRUCTOR - Internal staff who create content
   * Can: Everything Premium/Student can + create/edit courses, manage content
   */
  INSTRUCTOR = 'instructor',

  /**
   * ADMIN - System administrator
   * Can: Everything + user management, system settings, analytics
   */
  ADMIN = 'admin',

  /**
   * SUSPENDED - Suspended account
   * Can: Nothing - account access revoked
   */
  SUSPENDED = 'suspended',
}

export type SimpleRole = UserRole;

// Helper: Check if role has admin privileges
export function isAdmin(role: UserRole): boolean {
  return role === UserRole.ADMIN;
}

// Helper: Check if role has instructor privileges
export function isInstructor(role: UserRole): boolean {
  return role === UserRole.INSTRUCTOR || role === UserRole.ADMIN;
}

// Helper: Check if role has premium membership (premium, instructor, admin)
// Use for: Platform features like library, avatars, chamber of echoes
// Does NOT include 'student' - that's a course purchase status, not membership tier
export function isPremium(role: UserRole): boolean {
  return role === UserRole.PREMIUM || role === UserRole.INSTRUCTOR || role === UserRole.ADMIN;
}

// DEPRECATED: Use isPremium for platform features, or check purchases table for course access
// This function includes 'student' role which is being phased out
export function hasSubscription(role: UserRole): boolean {
  return (
    role === UserRole.PREMIUM ||
    role === UserRole.STUDENT ||
    role === UserRole.INSTRUCTOR ||
    role === UserRole.ADMIN
  );
}

// DEPRECATED: Use isPremium instead
export function isSubscriberOrHigher(role: UserRole): boolean {
  return hasSubscription(role);
}

// Helper: Check if role is suspended
export function isSuspended(role: UserRole): boolean {
  return role === UserRole.SUSPENDED;
}

// Role hierarchy (for UI/display purposes)
export const ROLE_LEVELS: Record<UserRole, number> = {
  [UserRole.SUSPENDED]: -1,
  [UserRole.FREE]: 0,
  [UserRole.PREMIUM]: 1,
  [UserRole.STUDENT]: 1, // Same level as Premium
  [UserRole.INSTRUCTOR]: 2,
  [UserRole.ADMIN]: 3,
};

// User labels for UI
// BRITE collapses WRA's 6 roles to 3 display labels (DB values unchanged):
// free/premium/student -> Member, instructor -> Manager, admin -> Admin.
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.FREE]: 'Member',
  [UserRole.PREMIUM]: 'Member',
  [UserRole.STUDENT]: 'Member',
  [UserRole.INSTRUCTOR]: 'Manager',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.SUSPENDED]: 'Suspended',
};

/** BRITE manager check — manager tier = stored 'instructor' role (or admin). */
export function isManagerRole(role: UserRole | string): boolean {
  return role === UserRole.INSTRUCTOR || role === UserRole.ADMIN;
}

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.FREE]: 'Enrolled member',
  [UserRole.PREMIUM]: 'Member with active membership',
  [UserRole.STUDENT]: 'Member with course access',
  [UserRole.INSTRUCTOR]: 'Manager — intake, PINs, events, Generator, user list',
  [UserRole.ADMIN]: 'Admin — full access incl. payment confirm and roles',
  [UserRole.SUSPENDED]: 'Account access suspended',
};
